import { useEffect, useRef } from 'react';
import { svgPrefetch } from '../utils/svg-prefetch';
import type { SvgLoadType } from '../components/esim-card-background';

/**
 * Hook to prefetch eSIM SVG images for phone numbers
 *
 * Prefetches SVG images when phone numbers are provided,
 * preventing flickering when rendering EsimCardPreview components.
 *
 * @param phoneNumbers - Array of phone numbers (msisdn format)
 * @param types - Types of SVG to prefetch (default: ['body'])
 * @param enabled - Whether prefetching is enabled (default: true)
 */
export function usePrefetchEsimSvgs(
	phoneNumbers: string[] | undefined,
	types: SvgLoadType[] = ['body'],
	enabled: boolean = true
): void {
	const prefetchedRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (!enabled || !phoneNumbers || phoneNumbers.length === 0) {
			return;
		}

		const newPhoneNumbers = phoneNumbers.filter(
			(phone) => !prefetchedRef.current.has(phone)
		);

		if (newPhoneNumbers.length === 0) {
			return;
		}

		svgPrefetch.prefetchForPhoneNumbers(newPhoneNumbers, types);

		for (const phone of newPhoneNumbers) {
			prefetchedRef.current.add(phone);
		}
	}, [phoneNumbers, types, enabled]);
}

/**
 * Hook to prefetch eSIM SVG for a single phone number
 *
 * @param phoneNumber - Phone number (msisdn format)
 * @param types - Types of SVG to prefetch (default: ['body'])
 * @param enabled - Whether prefetching is enabled (default: true)
 */
export function usePrefetchEsimSvg(
	phoneNumber: string | undefined,
	types: SvgLoadType[] = ['body'],
	enabled: boolean = true
): void {
	const prefetchedRef = useRef<string | null>(null);

	useEffect(() => {
		if (!enabled || !phoneNumber || phoneNumber === prefetchedRef.current) {
			return;
		}

		svgPrefetch.prefetchForPhoneNumber(phoneNumber, types);
		prefetchedRef.current = phoneNumber;
	}, [phoneNumber, types, enabled]);
}

/**
 * Check if SVG for a phone number is already cached
 */
export function useIsSvgCached(
	phoneNumber: string | undefined,
	type: SvgLoadType = 'body'
): boolean {
	if (!phoneNumber) return false;
	return svgPrefetch.isCached(phoneNumber, type);
}

